"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import Loader2 from "lucide-react/dist/esm/icons/loader";
import Plus from "lucide-react/dist/esm/icons/plus";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import ImagePlus from "lucide-react/dist/esm/icons/image-plus";
import X from "lucide-react/dist/esm/icons/x";
import FileQuestion from "lucide-react/dist/esm/icons/file-question";
import Handshake from "lucide-react/dist/esm/icons/handshake";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/contexts/SessionContext";
import { useProfile } from "@/contexts/ProfileContext";
import { useCommissionFeeRate, useCreateDirectContract, useCreateRequest } from "@/hooks/useCommissions";
import { humanizeError } from "@/lib/error-humanizer";
import { isCommissionBanned } from "@/lib/commission-standing";
import { CommissionBanNotice } from "@/components/moderation/CommissionBanNotice";
import { commissionsHref } from "@/lib/commissions/routes";
import { appendReferenceImages } from "@/lib/commissions/request-references";
import { commissionEarningsOn, commissionFeeOn } from "./commission-money";
import { todayForDateInput } from "./commission-next-action";
import { cn } from "@/lib/utils";

export type HireMode = "quote" | "offer";
export type HireVariant = "default" | "thumbnail";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sellerId: string;
  sellerName: string;
  startingPrice?: number | null;
  /** The creator's floor. A DB trigger rejects offers below it, so block them here first. */
  minBudget?: number | null;
  /** Which tab opens first. Quote is the default: most buyers don't know the going rate. */
  initialMode?: HireMode;
  variant?: HireVariant;
}

interface MilestoneInput {
  title: string;
  description: string;
  amount: string;
}

const MAX_IMAGES = 5;
const MAX_IMAGE_SIZE = 20 * 1024 * 1024;

const COPY = {
  default: {
    titlePlaceholder: "e.g. Custom 1v1 map with 4 arenas",
    descPlaceholder: "Scope, style, references, and what “done” looks like. The more concrete, the better the price you get back.",
  },
  thumbnail: {
    titlePlaceholder: "e.g. Zone Wars map thumbnail",
    descPlaceholder: "Map name, style, mood, colours, characters, text overlay, resolution…",
  },
} as const;

/**
 * Hire a creator — one dialog, two ways in.
 *
 *  - **Ask for a quote**: the buyer describes the job and the CREATOR names the
 *    price. Creates a `commission_request` with `target_seller_id`, which stays
 *    off the public board and runs through the proposal → contract path.
 *  - **Send an offer**: the buyer names the price. Creates a contract in
 *    `pending_acceptance` the creator accepts or declines.
 *
 * These used to be two separate modals (HireMeModal / RequestQuoteModal) that
 * different pages offered inconsistently — the board only had the one that
 * made buyers guess a number — and both dumped you on the dashboard afterwards
 * instead of on the thing you had just created.
 */
export function HireCreatorDialog({
  open,
  onOpenChange,
  sellerId,
  sellerName,
  startingPrice,
  minBudget,
  initialMode = "quote",
  variant = "default",
}: Props) {
  const router = useRouter();
  const { user } = useSession();
  const { profile } = useProfile();
  const createRequest = useCreateRequest();
  const createContract = useCreateDirectContract();
  // What the fee trigger will actually charge this creator (Max tier pays less).
  const feeRate = useCommissionFeeRate(sellerId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<HireMode>(initialMode);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [deadline, setDeadline] = useState("");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const floor = minBudget ?? null;
  const [price, setPrice] = useState((minBudget ?? startingPrice)?.toString() || "");
  const [images, setImages] = useState<{ file: File; preview: string }[]>([]);
  const [useMilestones, setUseMilestones] = useState(false);
  const [milestones, setMilestones] = useState<MilestoneInput[]>([{ title: "", description: "", amount: "" }]);
  // After sending, the dialog says what happens next instead of vanishing.
  // An offer in particular needs the buyer BACK: once the creator accepts,
  // nothing starts until they fund escrow.
  const [sent, setSent] = useState<{ mode: HireMode; href: string } | null>(null);

  // Reopening for a different creator (or from a different button) should
  // land on the tab that button promised.
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setSent(null);
    }
  }, [open, initialMode]);

  const isSelf = profile?.id === sellerId;
  const copy = COPY[variant];
  const pending = createRequest.isPending || createContract.isPending;

  const totalPrice = parseFloat(price) || 0;
  const belowFloor = floor !== null && totalPrice > 0 && totalPrice < floor;
  const milestoneTotal = milestones.reduce((s, m) => s + (parseFloat(m.amount) || 0), 0);
  const milestonesMismatch = useMilestones && totalPrice > 0 && Math.abs(milestoneTotal - totalPrice) > 0.01;

  const reset = () => {
    setTitle("");
    setDescription("");
    setDeadline("");
    setBudgetMin("");
    setBudgetMax("");
    setPrice((minBudget ?? startingPrice)?.toString() || "");
    images.forEach((i) => URL.revokeObjectURL(i.preview));
    setImages([]);
    setUseMilestones(false);
    setMilestones([{ title: "", description: "", amount: "" }]);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const valid = files.slice(0, MAX_IMAGES - images.length).filter((f) => {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} is not an image`);
        return false;
      }
      if (f.size > MAX_IMAGE_SIZE) {
        toast.error(`${f.name} is over 20 MB`);
        return false;
      }
      return true;
    });
    setImages((prev) => [...prev, ...valid.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (index: number) =>
    setImages((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });

  /** Upload the picked images into the caller's own folder of the (private) references bucket; the public-style URL is kept as an identifier and signed on render. */
  const uploadImages = async (folder: string): Promise<string[]> => {
    if (!user || images.length === 0) return [];
    const urls: string[] = [];
    for (const img of images) {
      // Sanitised: the URL is later parsed back out of the description as one
      // whitespace-free token, so a space in a filename would drop every image.
      const ext = (img.file.name.includes(".") ? img.file.name.split(".").pop() ?? "" : "").replace(/[^a-z0-9]/gi, "").slice(0, 5).toLowerCase() || "jpg";
      const path = `${user.id}/${folder}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("commission-references").upload(path, img.file, { contentType: img.file.type });
      if (error) continue;
      const { data } = supabase.storage.from("commission-references").getPublicUrl(path);
      if (data?.publicUrl) urls.push(data.publicUrl);
    }
    return urls;
  };

  /** Reference images go into the contract chat as its first message. */
  const attachImages = async (contractId: string) => {
    if (!user || !profile?.id || images.length === 0) return;
    const urls = await uploadImages(contractId);
    if (urls.length === 0) {
      toast.warning("Your offer was sent, but the reference images didn't upload", {
        description: "Add them in the contract chat.",
      });
      return;
    }
    await supabase.from("commission_messages" as any).insert({
      contract_id: contractId,
      sender_id: profile.id,
      content: `📎 **Reference images attached:**\n\n${urls.map((u) => `![reference](${u})`).join("\n")}`,
      is_system: false,
    });
  };

  const submitQuote = async () => {
    const min = parseFloat(budgetMin) || 0;
    const max = parseFloat(budgetMax) || 0;
    if (min && max && max < min) {
      toast.error("The top of your budget can't be below the bottom");
      return;
    }
    // A quote request has no chat until it becomes a contract, so its images
    // ride in the description (lib/commissions/request-references).
    const refUrls = await uploadImages(`requests/${crypto.randomUUID()}`);
    if (images.length > 0 && refUrls.length < images.length) {
      toast.warning("Some reference images didn't upload", { description: "You can describe them in the request instead." });
    }
    const { requestId } = await createRequest.mutateAsync({
      title: title.trim(),
      description: appendReferenceImages(description.trim(), refUrls),
      // 0/0 is the "you tell me" case — see hasBudgetHint().
      budget_min: min,
      budget_max: max || min,
      deadline: deadline || undefined,
      target_seller_id: sellerId,
    });
    reset();
    setSent({ mode: "quote", href: requestId ? commissionsHref.request(requestId) : commissionsHref.mine });
  };

  const submitOffer = async () => {
    if (!totalPrice || totalPrice <= 0) {
      toast.error("Enter the price you're offering");
      return;
    }
    if (belowFloor) {
      toast.error(`${sellerName} takes commissions from $${floor}`, { description: "Raise the price to send this offer." });
      return;
    }
    const validMilestones = milestones.filter((m) => m.title.trim() && parseFloat(m.amount) > 0);
    if (useMilestones) {
      if (validMilestones.length < 2) {
        toast.error("Add at least 2 milestones with a title and an amount");
        return;
      }
      if (milestonesMismatch) {
        toast.error(`Milestones must add up to the total price ($${totalPrice})`);
        return;
      }
    }
    const { contractId } = await createContract.mutateAsync({
      seller_id: sellerId,
      title: title.trim(),
      description: description.trim(),
      price: totalPrice,
      deadline: deadline || undefined,
      milestones: useMilestones
        ? validMilestones.map((m) => ({
            title: m.title.trim(),
            description: m.description.trim() || undefined,
            amount: parseFloat(m.amount),
          }))
        : undefined,
    });
    if (contractId) await attachImages(contractId);
    reset();
    setSent({ mode: "offer", href: contractId ? commissionsHref.contract(contractId) : commissionsHref.mine });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.error("Add a title and a description so there's something to price");
      return;
    }
    try {
      if (mode === "quote") await submitQuote();
      else await submitOffer();
    } catch (err) {
      toast.error(mode === "quote" ? "Couldn't send the quote request" : "Couldn't send the offer", {
        description: humanizeError(err, mode === "quote" ? "sending the quote request" : "creating the contract"),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto p-0 sm:max-w-xl">
        <div className="border-b border-border p-5 pb-4">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-xl">
              {sent ? (sent.mode === "quote" ? `Quote request sent to ${sellerName}` : `Offer sent to ${sellerName}`) : `Hire ${sellerName}`}
            </DialogTitle>
            <DialogDescription>
              {sent
                ? "Here's what happens next."
                : mode === "quote"
                ? `Describe the job. ${sellerName} replies with a price and a timeline, and you decide from there.`
                : `Name your price. ${sellerName} accepts or declines, and you only pay once they accept.`}
            </DialogDescription>
          </DialogHeader>

          {!sent && !isSelf && !isCommissionBanned(profile) && (
            <div role="radiogroup" aria-label="How do you want to hire?" className="mt-4 grid grid-cols-2 gap-2">
              {(
                [
                  { id: "quote", icon: FileQuestion, label: "Get a quote", hint: "They name the price. Best if you're not sure what it costs." },
                  { id: "offer", icon: Handshake, label: "Make an offer", hint: "You name the price. They accept or decline." },
                ] as const
              ).map((opt) => {
                const selected = mode === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setMode(opt.id)}
                    className={cn(
                      "flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      selected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50",
                    )}
                  >
                    <opt.icon className={cn("mt-0.5 h-4 w-4 shrink-0", selected ? "text-primary" : "text-muted-foreground")} aria-hidden />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{opt.label}</span>
                      <span className="block text-xs text-muted-foreground">{opt.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {sent ? (
          <div className="space-y-4 p-5">
            <ol className="space-y-3 text-sm">
              {(sent.mode === "quote"
                ? [
                    `${sellerName} replies with a price and a delivery time. We'll notify you.`,
                    "Accept it, counter it, or decline. Nothing is charged until you accept.",
                    "Accepting takes you to checkout. The money is held in escrow until you approve the work.",
                  ]
                : [
                    `${sellerName} accepts or declines your offer. We'll notify you either way.`,
                    "Once they accept, come back and fund escrow. Work starts when it's funded, not before.",
                    "Approve the delivery and the payment is released to them.",
                  ]
              ).map((line, i) => (
                <li key={line} className="flex gap-2.5">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/40 font-mono-accent text-xs text-primary">
                    {i + 1}
                  </span>
                  <span className="pt-0.5 text-muted-foreground">{line}</span>
                </li>
              ))}
            </ol>
            {sent.mode === "offer" && (
              <p className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-foreground">
                Your next move comes after they accept. It shows under <strong>Needs you</strong> in My commissions.
              </p>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                className="flex-1"
                onClick={() => {
                  onOpenChange(false);
                  router.push(sent.href);
                }}
              >
                {sent.mode === "quote" ? "View my request" : "Open the contract"}
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
                Keep browsing
              </Button>
            </div>
          </div>
        ) : isCommissionBanned(profile) ? (
          <div className="p-5">
            <CommissionBanNotice />
          </div>
        ) : isSelf ? (
          <p className="p-6 text-center text-sm text-muted-foreground">You can&apos;t hire yourself.</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 p-5">
            <div className="space-y-2">
              <Label htmlFor="hire-title">What do you need?</Label>
              <Input id="hire-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={copy.titlePlaceholder} maxLength={200} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hire-desc">Details</Label>
              <Textarea id="hire-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={copy.descPlaceholder} rows={5} maxLength={5000} />
            </div>

            {mode === "quote" ? (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Budget idea <span className="font-normal text-muted-foreground">(optional)</span></legend>
                <p className="text-xs text-muted-foreground">
                  Just a steer. {sellerName} sets the final price, so leave it blank if you&apos;d rather hear their number first.
                </p>
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} step="1" inputMode="decimal" value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)} placeholder="Min $" aria-label="Budget minimum" />
                  <span className="text-muted-foreground" aria-hidden>–</span>
                  <Input type="number" min={0} step="1" inputMode="decimal" value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} placeholder="Max $" aria-label="Budget maximum" />
                </div>
              </fieldset>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="hire-price">Your price (USD)</Label>
                <Input
                  id="hire-price"
                  type="number"
                  min={floor ?? 1}
                  step="1"
                  inputMode="decimal"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder={floor ? `From $${floor}` : startingPrice ? `From $${startingPrice}` : "100"}
                  aria-invalid={belowFloor}
                />
                {floor !== null && (
                  <p className={cn("text-xs", belowFloor ? "text-destructive" : "text-muted-foreground")}>
                    {sellerName} takes commissions from <strong>${floor}</strong>.
                  </p>
                )}
                {totalPrice > 0 && (
                  <dl className="mt-2 space-y-1 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs">
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">You pay (held in escrow)</dt>
                      <dd className="font-mono font-semibold">${totalPrice.toFixed(2)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Platform fee ({Math.round(feeRate * 100)}%)</dt>
                      <dd className="font-mono">−${commissionFeeOn(totalPrice, feeRate).toFixed(2)}</dd>
                    </div>
                    <div className="flex justify-between border-t border-border/50 pt-1 font-medium">
                      <dt>{sellerName} receives</dt>
                      <dd className="font-mono text-primary">${commissionEarningsOn(totalPrice, feeRate).toFixed(2)}</dd>
                    </div>
                  </dl>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="hire-deadline">
                Needed by <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input id="hire-deadline" type="date" min={todayForDateInput()} value={deadline} onChange={(e) => setDeadline(e.target.value)} />
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">
                Reference images <span className="font-normal text-muted-foreground">(optional)</span>
              </p>
              {images.length > 0 && (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                  {images.map((img, i) => (
                    <div key={img.preview} className="group relative aspect-square overflow-hidden rounded-md border border-border/50 bg-muted/30">
                      <Image src={img.preview} alt={`Reference ${i + 1}`} fill sizes="20vw" unoptimized className="object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        aria-label={`Remove reference image ${i + 1}`}
                        className="absolute right-0.5 top-0.5 rounded-full bg-background/80 p-0.5 backdrop-blur-sm"
                      >
                        <X className="h-3 w-3" aria-hidden />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {images.length < MAX_IMAGES && (
                <>
                  <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelect} />
                  <Button type="button" variant="outline" size="sm" className="w-full border-dashed" onClick={() => fileInputRef.current?.click()}>
                    <ImagePlus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Add images ({images.length}/{MAX_IMAGES})
                  </Button>
                </>
              )}
            </div>

            {mode === "offer" && (
              <>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 p-3">
                  <div>
                    <p className="text-sm font-medium">Pay in milestones</p>
                    <p className="text-xs text-muted-foreground">Fund and release the work in stages</p>
                  </div>
                  <Switch checked={useMilestones} onCheckedChange={setUseMilestones} aria-label="Pay in milestones" />
                </div>

                {useMilestones && (
                  <div className="space-y-3 rounded-lg border border-border/60 p-3">
                    {milestones.map((m, i) => (
                      <div key={i} className="space-y-2 rounded-md bg-muted/30 p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs text-muted-foreground">Milestone {i + 1}</span>
                          {milestones.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" aria-label={`Remove milestone ${i + 1}`} onClick={() => setMilestones((p) => p.filter((_, idx) => idx !== i))}>
                              <Trash2 className="h-3 w-3" aria-hidden />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                          <Input className="sm:col-span-2" placeholder="Milestone title" aria-label={`Milestone ${i + 1} title`} value={m.title} maxLength={100} onChange={(e) => setMilestones((p) => p.map((x, idx) => (idx === i ? { ...x, title: e.target.value } : x)))} />
                          <Input type="number" min="1" step="1" placeholder="$" aria-label={`Milestone ${i + 1} amount`} value={m.amount} onChange={(e) => setMilestones((p) => p.map((x, idx) => (idx === i ? { ...x, amount: e.target.value } : x)))} />
                        </div>
                        <Input placeholder="Short description (optional)" aria-label={`Milestone ${i + 1} description`} value={m.description} maxLength={500} onChange={(e) => setMilestones((p) => p.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)))} />
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => setMilestones((p) => [...p, { title: "", description: "", amount: "" }])}>
                      <Plus className="mr-1 h-3 w-3" aria-hidden /> Add milestone
                    </Button>
                    {totalPrice > 0 && (
                      <p className={cn("text-center font-mono text-xs", milestonesMismatch ? "text-destructive" : "text-muted-foreground")}>
                        ${milestoneTotal.toFixed(2)} of ${totalPrice.toFixed(2)}
                        {milestonesMismatch ? " — must match the total" : " ✓"}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="space-y-2">
              <Button type="submit" className="w-full" disabled={pending || (mode === "offer" && belowFloor)}>
                {pending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    Sending…
                  </>
                ) : mode === "quote" ? (
                  "Send quote request"
                ) : (
                  "Send offer"
                )}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                {mode === "quote"
                  ? "Nothing is charged now. You only pay if you accept their quote."
                  : "Nothing is charged now. Payment is held in escrow once they accept."}
              </p>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
